import React from "react";
import Lottie from "lottie-react";
import { useLocation } from "react-router-dom";
import { Container, Row, Col, Button, Alert } from "react-bootstrap";
import {
    planDetailsCopy,
    PlanName,
    ApiPlan,
    ThankYouLocationState,
} from "./types";
import { usePlans } from "./queries";
import PaymentPageLoadingGif from "../../assets/payment-page-loading.gif";
import loadingIcon from "./loading-icon.json";
import RecommendedHabi from "../../assets/all-plans-guided-habi.png";
import ThriveCoreHeader from "../../assets/thrive-core-header.png";
import ThriveProHeader from "../../assets/thrive-pro-header.png";
import ThrivePlusHeader from "../../assets/thrive-plus-header.png";
import ThriveCoreFeatures from "../../assets/thrive-core-features.png";
import ThrivePlusFeatures from "../../assets/thrive-plus-features.png";
import ThriveProFeatures from "../../assets/thrive-pro-features.png";
import HelpCard from "./HelpCard";
import PaymentStepModal from "./PaymentStepModal";
import { ThankYou } from "./ThankYou";
import "./style.scss";

// Helper functions to get images based on plan name
function getHeaderImage(planName: string) {
    if (planName === "Thrive Core") return ThriveCoreHeader;
    if (planName === "Thrive Plus") return ThrivePlusHeader;
    return ThriveProHeader;
}

function getFeaturesImage(planName: string) {
    if (planName === "Thrive Core") return ThriveCoreFeatures;
    if (planName === "Thrive Plus") return ThrivePlusFeatures;
    return ThriveProFeatures;
}

export function AllPlans({
    jotformId = "",
    userId = "",
    rcPlan,
    fromMobile = false,
}: {
    jotformId?: string;
    userId?: string;
    rcPlan?: string;
    fromMobile?: boolean;
} = {}) {
    const location = useLocation();
    let rcPlanParam = rcPlan;
    if (!rcPlanParam) {
        const search = new URLSearchParams(location.search);
        rcPlanParam = search.get("rc_plan") || "";
    }

    const search = new URLSearchParams(location.search);
    const planIdFromUrl = search.get("plan_id") || "";

    const { data, isLoading, error } = usePlans();

    const matchedPlans = (data?.plans ?? []).filter((p) =>
        Object.keys(planDetailsCopy).includes(p.name)
    ) as (ApiPlan & { name: PlanName })[];

    // Only highlight recommended, no selection
    let recommendedPlanId: string | undefined;
    let showRecommendedBadge = false;
    if (rcPlanParam) {
        const rcPlanLower = rcPlanParam.toLowerCase();
        const found = matchedPlans.find((p) =>
            p.name ? p.name.toLowerCase().includes(rcPlanLower) : false
        );
        recommendedPlanId = found?.id;
        showRecommendedBadge = !!recommendedPlanId;
    }
    if (!recommendedPlanId && rcPlanParam) {
        recommendedPlanId =
            matchedPlans.length > 1 ? matchedPlans[1].id : matchedPlans[0]?.id;
        showRecommendedBadge = !!recommendedPlanId;
    }

    const [showPaymentModal, setShowPaymentModal] = React.useState(false);
    const [modalManuallyClosed, setModalManuallyClosed] = React.useState(false);
    const [showThankYou, setShowThankYou] = React.useState(false);
    const [thankYouState, setThankYouState] =
        React.useState<ThankYouLocationState | null>(null);
    const [subscriptionLoading, setSubscriptionLoading] = React.useState(false);
    const [currentTextIdx, setCurrentTextIdx] = React.useState(0);

    const [selectedPlanId, setSelectedPlanId] = React.useState<string>(
        planIdFromUrl || recommendedPlanId || ""
    );

    const rotatingTexts = [
        "Processing your payment...",
        "Your wellness journey is loading...",
        "We’re confirming your transaction...",
        "Please do not close the window...",
        "Your health transformation starts soon!",
    ];

    // Open modal automatically if planIdFromUrl is present and valid (only once)
    React.useEffect(() => {
        if (
            planIdFromUrl &&
            matchedPlans.some((p) => p.id === planIdFromUrl) &&
            !modalManuallyClosed
        ) {
            setShowPaymentModal(true);
        }
    }, [planIdFromUrl, matchedPlans, modalManuallyClosed]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTextIdx((idx) => (idx + 1) % rotatingTexts.length);
        }, 4200);
        return () => clearInterval(interval);
    }, []);

    const apiPlans = React.useMemo(() => {
        if (data && Array.isArray(data.plans)) {
            return data.plans.reduce(
                (acc: Record<string, ApiPlan>, plan: ApiPlan) => {
                    acc[plan.id] = plan;
                    return acc;
                },
                {}
            );
        }
        return {};
    }, [data]);

    if (showThankYou || thankYouState) {
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        return <ThankYou {...thankYouState} fromMobile={fromMobile} />;
    }

    if (isLoading) {
        return (
            <div className="loading-page-center">
                <img
                    src={PaymentPageLoadingGif}
                    alt="Loading..."
                    className="loading-page-gif"
                />
            </div>
        );
    }
    if (isLoading) {
        return (
            <div className="loading-page-center">
                <Lottie
                    animationData={loadingIcon}
                    autoPlay
                    loop
                    className="loading-lottie"
                />
            </div>
        );
    }

    if (subscriptionLoading) {
        return (
            <div
                className="loading-page-center"
                style={{ flexDirection: "column" }}
            >
                <Lottie
                    animationData={loadingIcon}
                    autoPlay
                    loop
                    className="loading-lottie"
                />
                <div
                    style={{
                        marginTop: 14,
                        fontSize: 24,
                        color: "#00087a",
                        fontWeight: 600,
                        minHeight: 32,
                        textAlign: "center",
                        transition: "opacity 0.5s",
                    }}
                >
                    {rotatingTexts[currentTextIdx]}
                </div>
            </div>
        );
    }

    return (
        <Container className="pricing-page">
            <div className="plans-header-section">
                <h2 className="pricing-hero-title">
                    Pick Your
                    <span className="pricing-hero-section-flexible">
                        Expert Support Level.
                    </span>
                </h2>
                <div className="pricing-hero-subtitle">
                    Whether you want to lose weight naturally, manage metabolic
                    health, or work with GLP-1 therapy, we’ve built three tiers
                    for different needs:
                </div>
                <img
                    src={RecommendedHabi}
                    className="recommended-habi"
                    alt="Recommended Habi"
                />
            </div>

            {error && <Alert variant="danger">{error.message}</Alert>}

            <Row className="plans-row">
                {/* Render all plans in order, CSS will handle reordering on mobile */}
                {matchedPlans.map((plan) => {
                    const isRecommended =
                        showRecommendedBadge && plan.id === recommendedPlanId;
                    let cardClass = "thrive-plan-card-wrapper";
                    if (isRecommended) {
                        cardClass += " selected-card";
                    }
                    const headerImg = getHeaderImage(plan.name);
                    const featuresImg = getFeaturesImage(plan.name);

                    return (
                        <Col
                            className={`plan-col${
                                isRecommended ? " recommended-plan-col" : ""
                            }`}
                            key={plan.id}
                        >
                            <div className={cardClass}>
                                {isRecommended && (
                                    <div className="recommended-badge">
                                        Recommended
                                    </div>
                                )}
                                <div className="plan-card-header">
                                    <div className="plan-feature-span">
                                        <img
                                            src={headerImg}
                                            alt={plan.name}
                                            className="plan-header-image"
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="price-heading">
                                        {plan.name === "Thrive Pro" ? (
                                            <div className="thrive-pro-price-row">
                                                <span>
                                                    ₹
                                                    {(
                                                        plan.amount / 100
                                                    ).toLocaleString("en-IN")}
                                                    <span className="plan-price-per-month">
                                                        /month
                                                    </span>
                                                </span>
                                                <span className="thrive-pro-cost-note">
                                                    + Cost of Medication
                                                    <br />
                                                    (15 - 20K/month)
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                ₹
                                                {(
                                                    plan.amount / 100
                                                ).toLocaleString("en-IN")}
                                                <span className="plan-price-per-month">
                                                    /month
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <img
                                        src={featuresImg}
                                        alt={`${plan.name} Features`}
                                        style={{
                                            width: "100%",
                                            margin: "12px 0",
                                            borderRadius: 12,
                                        }}
                                    />
                                    <hr />
                                    <div className="plan-subscription-button">
                                        <Button
                                            className="global-start-btn"
                                            onClick={() => {
                                                setSelectedPlanId(plan.id);
                                                setShowPaymentModal(true);
                                            }}
                                        >
                                            Start Subscription
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {/* Help Card */}
            <HelpCard fullwidth />

            {/* Payment Modal for selected plan */}
            <PaymentStepModal
                show={showPaymentModal}
                onHide={() => {
                    setShowPaymentModal(false);
                    setModalManuallyClosed(true);
                }}
                jotformId={jotformId}
                userId={userId}
                planId={selectedPlanId || ""}
                planName={
                    selectedPlanId && apiPlans[selectedPlanId]
                        ? (apiPlans[selectedPlanId].name as PlanName)
                        : null
                }
                setShowThankYou={setShowThankYou}
                setThankYouState={setThankYouState}
                setSubscriptionLoading={setSubscriptionLoading}
                fromMobile={fromMobile}
            />
        </Container>
    );
}
